import { Component, OnInit } from "@angular/core";
import { BaseListDirective } from "../../../util/directives/base-list.directive";
import { EntityTyp } from "../../../util/enums/entity-typ.enum";
import { ContentHeader } from "../../../layout/components/content-header/content-header.component";
import { ActivatedRoute, Router } from "@angular/router";
import { ModalService } from "../../../util/service/modal.service";
import { FormBuilder } from "@angular/forms";
import Swal from "sweetalert2";
import { colors } from "../../../colors.const";
import { ClientListItemDto, NameDto, OfficeNameDto } from "app/api/models";
import { ClientService } from "app/api/services";
import { AuthService } from "../../../util/service/auth.service";
import { UserRole } from "app/server/enums/user-role.enum";
import { formatDate } from "@angular/common";
import { OfficeType } from "app/server/enums/office-type.enum";
import { DownloadService } from "../../../util/service/download.service";
import { CaslAction } from "../../../server/enums/casl-action.enum";
import { ToastrService } from "ngx-toastr";

@Component({
    selector: "app-client-list",
    templateUrl: "./client-list.component.html",
    styleUrl: "./client-list.component.scss",
    standalone: false
})
export class ClientListComponent extends BaseListDirective<ClientListItemDto> implements OnInit {
  override modalType = EntityTyp.Client;
  protected readonly UserRole = UserRole;
  protected readonly CaslAction = CaslAction;
  protected readonly EntityTyp = EntityTyp;

  public contentHeader: ContentHeader;

  housings: NameDto[];
  offices: OfficeNameDto[];

  get isAdmin() {
    return this.authService.currentUserValue?.role === UserRole.Admin;
  }

  get isBueroleitung() {
    return this.authService.currentUserValue?.role === UserRole.Bueroleitung;
  }

  get hasGVSOffice() {
    return this.offices?.some(({ type }) => type === OfficeType.GVS);
  }

  constructor(
    protected override route: ActivatedRoute,
    protected override router: Router,
    private clientService: ClientService,
    protected override modalService: ModalService,
    protected override fb: FormBuilder,
    public authService: AuthService,
    public downloadService: DownloadService,
    private toastrService: ToastrService
  ) {
    super(fb, route, router, modalService);
  }

  ngOnInit(): void {
    this.offices = this.route.snapshot.data["offices"];
    this.housings = this.route.snapshot.data["housings"];

    this.contentHeader = {
      headerTitle: "Klient:innen",
      actionButton: false,
      breadcrumb: {
        type: "",
        links: [
          {
            name: "Startseite",
            isLink: true,
            link: "/",
            translate: "MENU.HOME",
          },
          {
            name: "Klient:innen",
            isLink: false,
            translate: "MENU.CLIENTS",
          },
        ],
      },
    };

    this.initFilter({
      firstName: [null],
      lastName: [null],
      birthday: [null],
      officeId: [null],
      housingId: [null],
      deleted: [false],
    });
  }

  reload() {
    this.clientService.clientControllerFindAll(this.filter.value).subscribe({
      next: (page) => {
        this.page = page;
        this.syncPageNumber();
      },
    });
  }

  async openModal(id?: number) {
    await this.modalService.openEntityModal(EntityTyp.Client, id);
    this.reload();
  }

  async delete(id) {
    const confirm = await Swal.fire({
      text: "Möchten Sie diese:n Klient:in wirklich löschen?",
      icon: "error",
      showCancelButton: true,
      focusCancel: true,
      confirmButtonColor: colors.solid.danger,
      confirmButtonText: "Löschen",
      cancelButtonText: "Abbrechen",
    });
    if (confirm.isConfirmed) {
      this.clientService.clientControllerDelete({ id }).subscribe((res) => {
        this.reload();
      });
    }
  }

  downloadKlientenliste() {
    this.clientService
      .clientControllerGenerateClientListExcel({ ...this.filter.value, ...{ page: 0, limit: Number.MAX_VALUE } })
      .subscribe((res) => {
        if (res.size === 4) {
          return false;
        }
        this.downloadService.handleDownloadedFile(
          res,
          `Klientenliste_${formatDate(Date.now(), "MM-dd-yyyy", "de-AT")}.xlsx`
        );
      });
  }
  async restore(id) {
    const confirm = await Swal.fire({
      text: "Möchten Sie diese:n Klient:in wirklich wiederherstellen?",
      icon: "question",
      showCancelButton: true,
      focusCancel: true,
      confirmButtonColor: colors.solid.info,
      confirmButtonText: "Wiederherstellen",
      cancelButtonText: "Abbrechen",
    });
    if (confirm.isConfirmed) {
      this.clientService.clientControllerRestore({ id }).subscribe((res) => {
        this.toastrService.success("Erfolgreich wiederhergestellt", "Erfolgreich wiederhergestellt");
        this.reload();
      });
    }
  }

  async openTimeframeModal(id: number, client: ClientListItemDto) {
    const timeframe = await this.modalService.openTimeframeModal();
    if (timeframe) {
      return this.downloadService.downloadKlientenbericht(id, client.firstName, client.lastName, timeframe);
    }
  }
}
